import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatPriceParts, type Product } from '../data/products';
import { productGalleryShots, productHeroShot } from '../lib/productMedia';
import { ProductGallery } from './ProductGallery';

export function ProductCard({ product }: { product: Product }) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const titleId = useId();
  const preview = productHeroShot(product);
  const galleryShots = productGalleryShots(product);
  const { symbol: priceSymbol, amount: priceAmount } = formatPriceParts(
    product.priceCents,
    product.currency,
  );
  const lightboxShots = galleryShots.length ? galleryShots : preview ? [preview] : [];
  const shotCount = lightboxShots.length;

  useEffect(() => {
    if (!galleryOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setGalleryOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [galleryOpen]);

  return (
    <>
      <article className="card product-card">
        <div className="product-card-top">
          <div className="product-icon">
            <span className="material-symbols-outlined">{product.icon}</span>
          </div>
          {product.badge && <span className="badge">{product.badge}</span>}
        </div>

        <div className="product-card-main">
          <div className="product-card-copy">
            <h3>{product.name}</h3>
            <p>{product.tagline}</p>
            <div className="product-meta">
              <span>{product.category}</span>
              <span>·</span>
              <span>v{product.version}</span>
              <span>·</span>
              <span>EspoCRM {product.compatibility.espocrm || '9.x / 10.x'}</span>
            </div>
          </div>

          {preview && (
            <button
              type="button"
              className="product-card-shot"
              onClick={() => setGalleryOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={galleryOpen}
              aria-label={`View ${product.name} screenshots`}
            >
              <img src={preview.src} alt="" loading="lazy" draggable={false} />
              {shotCount > 1 && (
                <span className="product-card-shot-count" aria-hidden="true">
                  {shotCount}
                </span>
              )}
              <span className="product-card-shot-hint" aria-hidden="true">
                <span className="material-symbols-outlined">photo_library</span>
              </span>
            </button>
          )}
        </div>

        <div className="product-card-footer">
          <div className="price">
            <span className="price-currency">{priceSymbol}</span>
            <span className="price-amount">{priceAmount}</span>
          </div>
          <Link className="btn btn-primary" to={`/marketplace/${product.slug}`}>
            View details
          </Link>
        </div>
      </article>

      {galleryOpen && (
        <div
          className="product-shot-overlay"
          role="presentation"
          onClick={() => setGalleryOpen(false)}
        >
          <div
            className="card product-shot-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="product-shot-panel-top">
              <div>
                <span className="eyebrow" style={{ marginBottom: '0.35rem' }}>
                  Screenshots
                </span>
                <h2 id={titleId}>{product.name}</h2>
              </div>
              <button
                type="button"
                className="product-shot-close"
                onClick={() => setGalleryOpen(false)}
                aria-label="Close screenshots"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <ProductGallery screenshots={lightboxShots} productName={product.name} showZoom={false} />

            <div className="product-shot-panel-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setGalleryOpen(false)}>
                Close
              </button>
              <Link className="btn btn-primary" to={`/marketplace/${product.slug}`}>
                View details
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
